import { logger } from "./logger";

export const LIBRARY_ASSET_FETCH_TIMEOUT_MS = 15000;
export const LIBRARY_ASSET_FETCH_RETRIES = 2;
const LIBRARY_ASSET_FETCH_RETRY_DELAYS_MS = [300, 900];

/** @param {AbortSignal | null | undefined} signal */
export const libraryAssetFetchThrowIfAborted = (signal) => {
  if (signal?.aborted) throw new globalThis.DOMException("Aborted", "AbortError");
};

const isRetryableAssetFetchStatus = (status) =>
  status === 408 || status === 429 || (status >= 500 && status <= 599);

const resolveLibraryAssetFetchCredentials = (assetPath) => {
  const browserWindow = globalThis.window;
  if (!browserWindow?.location?.href) return "omit";
  try {
    const assetUrl = new URL(assetPath, browserWindow.location.href);
    return assetUrl.origin === new URL(browserWindow.location.href).origin ? "same-origin" : "omit";
  } catch {
    return "omit";
  }
};

const waitForAssetRetryDelay = (delayMs, signal) =>
  new Promise((resolve, reject) => {
    libraryAssetFetchThrowIfAborted(signal);
    const timeoutDelayMs = Math.max(0, Number(delayMs) || 0);
    let timeoutId = null;
    let onAbort = null;
    const cleanup = () => {
      if (timeoutId != null) globalThis.clearTimeout(timeoutId);
      if (onAbort) signal?.removeEventListener("abort", onAbort);
    };
    onAbort = () => {
      cleanup();
      reject(signal.reason || new globalThis.DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    timeoutId = globalThis.setTimeout(() => {
      cleanup();
      resolve();
    }, timeoutDelayMs);
  });

export const fetchLibraryAssetBlobWithRetry = async (
  assetPath,
  {
    signal,
    timeoutMs = LIBRARY_ASSET_FETCH_TIMEOUT_MS,
    retries = LIBRARY_ASSET_FETCH_RETRIES,
    retryDelaysMs = LIBRARY_ASSET_FETCH_RETRY_DELAYS_MS,
  } = {}
) => {
  let lastError = null;
  const maxAttempts = Math.max(1, Math.floor(Number(retries) || 0) + 1);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    libraryAssetFetchThrowIfAborted(signal);

    const controller = new AbortController();
    let didTimeout = false;
    const abortFromParent = () => controller.abort(signal.reason);
    const timeoutId = globalThis.setTimeout(
      () => {
        didTimeout = true;
        controller.abort(
          new globalThis.DOMException("Library asset fetch timed out", "TimeoutError")
        );
      },
      Math.max(1, Math.floor(Number(timeoutMs) || LIBRARY_ASSET_FETCH_TIMEOUT_MS))
    );

    signal?.addEventListener("abort", abortFromParent, { once: true });

    try {
      const response = await fetch(assetPath, {
        cache: "no-store",
        credentials: resolveLibraryAssetFetchCredentials(assetPath),
        signal: controller.signal,
      });
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status} for ${assetPath}`);
        error.status = response.status;
        throw error;
      }
      return await response.blob();
    } catch (error) {
      if (signal?.aborted) throw error;

      lastError = error;
      const shouldRetry =
        didTimeout || error instanceof TypeError || isRetryableAssetFetchStatus(error?.status);
      const hasRetryRemaining = attempt < maxAttempts - 1;
      if (!shouldRetry || !hasRetryRemaining) {
        throw error;
      }

      logger.warn("[libraryAssetLoader] Retrying library asset fetch", {
        assetPath,
        attempt: attempt + 1,
        maxAttempts,
        reason: error?.message || String(error),
      });
      await waitForAssetRetryDelay(retryDelaysMs[attempt] ?? 0, signal);
    } finally {
      globalThis.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", abortFromParent);
    }
  }

  throw lastError || new Error(`Failed to fetch library asset: ${assetPath}`);
};
